import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import type { TOC } from '@ember/component/template-only';
import type { ComponentLike } from '@glint/template';
import { routeModels } from '../utils/route-models';

/**
 * Visual intent/severity options
 */
export type ButtonIntent = 'primary' | 'brand' | 'danger' | 'warning' | 'success' | 'default';

/**
 * Visual treatment for button chrome.
 *
 * `link` preserves the minimal color and state treatment while removing the
 * horizontal inset so its label can align with surrounding prose.
 */
export type ButtonAppearance = 'default' | 'outlined' | 'minimal' | 'link';

/**
 * Size options
 */
export type ButtonSize = 'sm' | 'md' | 'lg';

type ButtonIcon = ComponentLike<{ Args: { size?: number | string }; Element: SVGElement }>;

export interface ButtonSignature {
  Args: {
    /**
     * Stable instance identity emitted as data-test-hook on the root. Automation identifies
     * descendants by this hook plus their role; choose a value that survives re-renders.
     */
    testHook?: string;
    /** HTML button type; ignored in the link modes (`@route` / `@href`). */
    type?: 'button' | 'submit' | 'reset';
    /** Visual intent */
    intent?: ButtonIntent;
    /** Appearance variant */
    appearance?: ButtonAppearance;
    /** Size */
    size?: ButtonSize;
    /** Show loading state (disables button, shows loading indicator) */
    isRunning?: boolean;
    /** Disabled state */
    isDisabled?: boolean;
    /** Icon component (Phosphor icon etc.) */
    icon?: ButtonIcon;
    /** Icon size override */
    iconSize?: number;
    /** Text label */
    label?: string;
    /** Accessible label for screen readers (important for icon-only buttons) */
    ariaLabel?: string;
    /** Icon placement */
    iconPlacement?: 'start' | 'end';
    /** Full width */
    isFullWidth?: boolean;
    /**
     * Route name to navigate to. Renders an Ember `<LinkTo>` wearing the
     * button recipe instead of a `<button>`, so a navigation CTA is a single
     * anchor rather than a button nested inside a link (invalid HTML, two tab
     * stops). Disabled / running state marks the anchor `aria-disabled` and
     * the link stays inert.
     */
    route?: string;
    /** Serialized ID for `@route`; passing IDs preserves destination data loading. */
    model?: string | number | null;
    /** Serialized IDs for `@route`, in path order. */
    models?: (string | number)[];
    /** Query params appended to the `@route` URL. */
    query?: Record<string, unknown>;
    /**
     * URL outside the router (external or absolute): renders a plain `<a>`
     * wearing the button recipe. Ignored when `@route` is set; `target` /
     * `rel` pass through as attributes. Dropped while disabled so the anchor
     * can be neither followed nor focused.
     */
    href?: string;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLButtonElement | HTMLAnchorElement;
}

// Class output is semantic: `tio-btn tio-btn--{intent} [tio-btn--{appearance}]
// tio-btn--{size}`. All color / state / shape intent lives in the `.tio-btn`
// family in `styles.css` (the filled appearance is the unmodified base, so it
// needs no appearance modifier). See that block for the light-hex-preserved
// recipe and the dark-mode token routing.

const ICON_SIZES: Record<ButtonSize, number> = {
  sm: 16,
  md: 18,
  lg: 22,
};

interface ContentSignature {
  Args: {
    isRunning?: boolean;
    icon?: ButtonIcon;
    iconSize: number;
    iconPlacement?: 'start' | 'end';
    label?: string;
  };
  Blocks: {
    default: [];
  };
}

const ButtonContent: TOC<ContentSignature> = <template>
  {{#if @isRunning}}
    <span class="tio-btn__loading" aria-label="Loading" role="status" data-test-button-loading>
      <span class="tio-btn__dot"></span>
      <span class="tio-btn__dot"></span>
      <span class="tio-btn__dot"></span>
    </span>
  {{/if}}

  <span
    data-test-button-content
    class="tio-btn__content {{if @isRunning 'tio-btn__content--hidden'}}"
  >
    {{#if @icon}}
      {{#if (eq @iconPlacement "end")}}
        {{#if @label}}
          <span>{{@label}}</span>
        {{else}}
          {{yield}}
        {{/if}}
        {{#let @icon as |Icon|}}
          <Icon @size={{@iconSize}} />
        {{/let}}
      {{else}}
        {{#let @icon as |Icon|}}
          <Icon @size={{@iconSize}} />
        {{/let}}
        {{#if @label}}
          <span>{{@label}}</span>
        {{else}}
          {{yield}}
        {{/if}}
      {{/if}}
    {{else if @label}}
      <span>{{@label}}</span>
    {{else}}
      {{yield}}
    {{/if}}
  </span>
</template>;

/**
 * Renders a button or a single anchor when route/href is supplied; loading state disables repeated
 * actions.
 */
export default class Button extends Component<ButtonSignature> {
  get intent(): ButtonIntent {
    return this.args.intent ?? 'default';
  }

  get appearance(): ButtonAppearance {
    return this.args.appearance ?? 'default';
  }

  get size(): ButtonSize {
    return this.args.size ?? 'md';
  }

  get isDisabled(): boolean {
    return this.args.isDisabled || this.args.isRunning || false;
  }

  get iconSize(): number {
    return this.args.iconSize ?? ICON_SIZES[this.size] ?? 18;
  }

  get buttonClasses(): string {
    const intent = `tio-btn--${this.intent}`;
    // The `default` (filled) appearance is the unmodified base — no modifier.
    const appearance = this.appearance === 'default' ? '' : `tio-btn--${this.appearance}`;
    const size = `tio-btn--${this.size}`;
    const width = this.args.isFullWidth ? 'tio-btn--full' : '';

    return ['tio-btn', intent, appearance, size, width].filter(Boolean).join(' ');
  }

  // LinkTo asserts on `@model` and `@models` both being *present*, even as undefined, so fold them into one arg.
  get linkModels() {
    return this.args.models ?? routeModels(this.args.model);
  }

  // LinkTo asserts `@query` is an object whenever the arg is present, so never hand it undefined.
  get linkQuery(): Record<string, unknown> {
    return this.args.query ?? {};
  }

  <template>
    {{#if @route}}
      <LinkTo
        @route={{@route}}
        @models={{this.linkModels}}
        @query={{this.linkQuery}}
        @disabled={{this.isDisabled}}
        class={{this.buttonClasses}}
        aria-disabled={{if this.isDisabled "true"}}
        aria-busy={{if @isRunning "true"}}
        aria-label={{@ariaLabel}}
        data-test-button
        data-test-hook={{@testHook}}
        data-test-button-intent={{this.intent}}
        data-test-button-appearance={{this.appearance}}
        data-test-button-size={{this.size}}
        ...attributes
      >
        <ButtonContent
          @isRunning={{@isRunning}}
          @icon={{@icon}}
          @iconSize={{this.iconSize}}
          @iconPlacement={{@iconPlacement}}
          @label={{@label}}
        >{{yield}}</ButtonContent>
      </LinkTo>
    {{else if @href}}
      {{! explicit role: a disabled anchor loses its href yet still reads as a link and may carry aria-disabled }}
      <a
        href={{unless this.isDisabled @href}}
        role="link"
        class={{this.buttonClasses}}
        aria-disabled={{if this.isDisabled "true"}}
        aria-busy={{if @isRunning "true"}}
        aria-label={{@ariaLabel}}
        data-test-button
        data-test-hook={{@testHook}}
        data-test-button-intent={{this.intent}}
        data-test-button-appearance={{this.appearance}}
        data-test-button-size={{this.size}}
        ...attributes
      >
        <ButtonContent
          @isRunning={{@isRunning}}
          @icon={{@icon}}
          @iconSize={{this.iconSize}}
          @iconPlacement={{@iconPlacement}}
          @label={{@label}}
        >{{yield}}</ButtonContent>
      </a>
    {{else}}
      <button
        type={{if @type @type "button"}}
        class={{this.buttonClasses}}
        disabled={{this.isDisabled}}
        aria-busy={{if @isRunning "true"}}
        aria-label={{@ariaLabel}}
        data-test-button
        data-test-hook={{@testHook}}
        data-test-button-intent={{this.intent}}
        data-test-button-appearance={{this.appearance}}
        data-test-button-size={{this.size}}
        ...attributes
      >
        <ButtonContent
          @isRunning={{@isRunning}}
          @icon={{@icon}}
          @iconSize={{this.iconSize}}
          @iconPlacement={{@iconPlacement}}
          @label={{@label}}
        >{{yield}}</ButtonContent>
      </button>
    {{/if}}
  </template>
}
